import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CustomTable from "../../components/Shared/CustomTable";
import PageHeader from "../../components/Shared/PageHeader";
import { getTransactionEntries } from "../../store/slices/TransactionsSlice";
import { handlePrintJournalEntries } from "./JournalEntriesPrint";
import SharedButton from "../../components/Shared/SharedButton";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const [year, month, day] = date.toISOString().split("T")[0].split("-");
  return `${day}-${month}-${year}`; // DD-MM-YYYY
};

export default function ViewTransactionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const transactionId = location?.state?.transactionId || null;
  const transactionDetails = location?.state?.transactionDetails || {};
  const { entries, isLoading, per_page } = useSelector(
    (state) => state.transaction,
  );
  const businessInfo = useSelector((state) => state?.auth?.user?.Business);

  useEffect(() => {
    if (transactionId) dispatch(getTransactionEntries(transactionId));
  }, [dispatch, transactionId]);

  const viewTansationColumns = [
    {
      field: "account_code",
      header: t("Account Code"),
      // pt: {
      //   headerCell:
      //     'bg-transparent !text-gray-900 !py-[0.5rem] !px-[0.3rem] border-t border-b border-gray-500 !font-semibold',
      //   bodyCell: 'text-gray-800 text-[14px] !py-[0.5rem] !px-[0.3rem] border-b border-gray-300'
      // }
    },
    {
      field: "account_name_en",
      header: t("AccountName"),
      // pt: {
      //   headerCell:
      //     'bg-transparent !text-gray-900 !py-[0.5rem] !px-[0.3rem] border-t border-b border-gray-500 !font-semibold',
      //   bodyCell: 'text-gray-800 text-[14px] !py-[0.5rem] !px-[0.3rem] border-b border-gray-300'
      // }
    },
    {
      field: "cost_center_name_en",
      header: t("costCenter"),
      // pt: {
      //   headerCell:
      //     'bg-transparent !text-gray-900 !py-[0.5rem] !px-[0.3rem] border-t border-b border-gray-500 !font-semibold',
      //   bodyCell: 'text-gray-800 text-[14px] !py-[0.5rem] !px-[0.3rem] border-b border-gray-300'
      // }
    },
    {
      header: t("Branch"),
      body: (data) => data?.branch_name_en || data?.location_name_en || "",
      // pt: {
      //   headerCell:
      //     'bg-transparent !text-gray-900 !py-[0.5rem] !px-[0.3rem] border-t border-b border-gray-500 !font-semibold',
      //   bodyCell: 'text-gray-800 text-[14px] !py-[0.5rem] !px-[0.3rem] border-b border-gray-300'
      // }
    },
    {
      field: "amount",
      header: t("debitAmount"),
      body: (rowData) =>
        rowData?.entry_type?.toLowerCase() === "debit"
          ? `${rowData?.amount} SAR`
          : "0 SAR",
      // pt: {
      //   headerCell:
      //     'bg-transparent !text-gray-900 !py-[0.5rem] !px-[0.3rem] border-t border-b border-gray-500 !font-semibold',
      //   bodyCell: 'text-gray-800 text-[14px] !py-[0.5rem] !px-[0.3rem] border-b border-gray-300'
      // }
    },
    {
      field: "payment_method",
      header: t("creditAmount"),
      body: (rowData) =>
        rowData?.entry_type?.toLowerCase() === "credit"
          ? `${rowData?.amount} SAR`
          : "0 SAR",
      // pt: {
      //   headerCell:
      //     'bg-transparent !text-gray-900 !py-[0.5rem] !px-[0.3rem] border-t border-b border-gray-500 !font-semibold',
      //   bodyCell: 'text-gray-800 text-[14px] !py-[0.5rem] !px-[0.3rem] border-b border-gray-300'
      // }
    },
    {
      field: "description",
      header: t("description"),
      // pt: {
      //   headerCell:
      //     'bg-transparent !text-gray-900 !py-[0.5rem] !px-[0.3rem] border-t border-b border-gray-500 !font-semibold',
      //   bodyCell: 'text-gray-800 text-[14px] !py-[0.5rem] !px-[0.3rem] border-b border-gray-300'
      // }
    },
  ];

  const totalDebit = entries?.reduce((sum, entry) => {
    return (
      sum +
      (entry.entry_type?.toLowerCase() === "debit"
        ? Number(entry.amount || 0)
        : 0)
    );
  }, 0);

  const totalCredit = entries?.reduce((sum, entry) => {
    return (
      sum +
      (entry.entry_type?.toLowerCase() === "credit"
        ? Number(entry.amount || 0)
        : 0)
    );
  }, 0);

  return (
    <div>
      <div className="flex justify-between items-center">
        <PageHeader title={t("Entries")} onBack={() => navigate(-1)} />
        <SharedButton
          icon="pi pi-print"
          label={t("print_journal_entries")}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-all"
          onClick={() =>
            handlePrintJournalEntries(entries, transactionDetails, t, isRTL, businessInfo)
          }
        />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <div className="header-left"></div>
          <div className="text-center">
            <div className="text-[22px] font-semibold">{businessInfo?.name_en}</div>
            <div className="text-center text-[16px]">{t("journal_entries")}</div>
          </div>
          <div className="text-[14px]">
            <div>
              <span className="font-semibold">{t("date")} :</span>{" "}
              <span className="text-sm">
                {new Date().toLocaleDateString("en-CA").replace(/-/g, "/")}
              </span>
            </div>
            <div>
              <span className="font-semibold">{t("page")} :</span>{" "}
              <span className="text-sm">
                {t("page_of", { page: 1, total: 1 })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-content-between w-full my-2">
          <div className="block w-full">
            <div>
              <span className="font-semibold">{t("record_no")}</span> :{" "}
              <span className="text-sm">
                {transactionDetails?.journal_entry_number || ""}
              </span>
            </div>
            <div>
              <span className="font-semibold">{t("description")}</span> :{" "}
              <span className="text-sm">
                {transactionDetails?.description || ""}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end w-full">
            <div>
              <span className="font-semibold">{t("date")}</span> :{" "}
              <span className="text-sm">
                {transactionDetails?.date
                  ? formatDate(transactionDetails.date)
                  : ""}
              </span>
            </div>
            <div>
              <span className="font-semibold">{t("period")}</span> :{" "}
              <span className="text-sm">
                {transactionDetails?.period ||
                  (transactionDetails?.updated_at
                    ? new Date(transactionDetails.updated_at)
                      .toLocaleDateString("en-US", {
                        month: "2-digit",
                        year: "numeric",
                      })
                      .replace("/", " - ")
                    : "")}
              </span>
            </div>
          </div>
        </div>
      </div>
      <CustomTable
        data={entries || []}
        columns={viewTansationColumns}
        rows={per_page}
        isLoading={isLoading}
      />
      <div className="flex items-center justify-end mt-4 w-full">
        <div>
          <div className="flex items-center justify-between mb-2 w-full md:w-[250px] capitalize">
            <span className="font-semibold">{t("total_debit")}:</span>
            <span>
              {totalDebit.toFixed(2)} {t("SAR")}
            </span>
          </div>
          <div className="flex items-center justify-between mb-2 w-full md:w-[250px] border-b-2 border-gray-600 pb-2">
            <span className="font-semibold">{t("total_credit")}:</span>
            <span>
              {totalCredit.toFixed(2)} {t("SAR")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
